import { ActionDisplayType, ActionKind, OrderItemStatus, SampleActionActor } from '@codezo/graphql';
import type { GetOrderQuery } from '@codezo/graphql';

type OrderItem = NonNullable<GetOrderQuery['getOrder']>['items'][number];
export type ItemAction = NonNullable<OrderItem['availableActions']>[number];

/**
 * Which flow row each move belongs under.
 *
 * ── Why this mapping has to exist ──
 *
 * `availableActions` is a flat list of what can be done to the ITEM right now.
 * Hanging that list on whichever row happens to be `current` put "Start
 * production" under **Sample & approval** — the step it has nothing to do with.
 * The design is explicit that content is per-stage (`OPFBStageContent` switches
 * on the stage), so a move belongs to the step it advances, not to the step the
 * item is standing on.
 *
 * Keyed by the status the action SETS — but the row it names is the step the
 * move COMPLETES, not the step it lands on. Those are the same row for most of
 * them and decisively different for one:
 *
 * ── Why "Mark as delivered" sat on a row it could never appear on ──
 *
 * DELIVERED was filed under the `delivered` row. That row is terminal: it only
 * becomes `current` once the item HAS been delivered, which is after the move
 * has been made. So the button was attached to a row that by definition never
 * needs it, and the Shipping row — the one the item actually sits on, waiting
 * for exactly that move — offered nothing at all. A whole leg of the flow was
 * unactionable.
 *
 * The design has it on Shipping: `stage === 5 && t === 5` renders the carrier,
 * the ETA and `Mark as delivered` together. The rule the rest of this table
 * already follows, stated properly: a move belongs to the step it finishes.
 *
 * Exhaustive over the enum, so a new status has to be placed here rather than
 * silently vanishing from the spine.
 */
const ROW_FOR_STATUS: Record<OrderItemStatus, string | null> = {
	[OrderItemStatus.InProduction]: 'production',
	[OrderItemStatus.ProductionCompleted]: 'production',
	[OrderItemStatus.Shipped]: 'shipping',
	[OrderItemStatus.PartiallyShipped]: 'shipping',
	// Finishes Shipping. See above - NOT 'delivered'.
	[OrderItemStatus.Delivered]: 'shipping',
	// Not steps, and deliberately NOT rendered in the spine. Cancelling is an
	// escape from the flow rather than a move along it — the design has no cancel
	// anywhere in the flow — so it lives on the product card, where v1 keeps it
	// too. PENDING is where an item starts and is never offered as a move.
	[OrderItemStatus.Cancelled]: null,
	[OrderItemStatus.Pending]: null,
};

export const rowForAction = (a: ItemAction): string | null =>
	a.status ? ROW_FOR_STATUS[a.status] : null;

/** A move that carries the item forward, as opposed to an escape or a property edit. */
export const isAdvancing = (a: ItemAction): boolean => a.kind === ActionKind.Move;

/**
 * What the studio can do to this item, split into where each part is rendered.
 *
 * `notices` are the server's standing statements — `INLINE_WIDGET` is it saying
 * "this is a message, not a button" — and carry no move.
 */
export function partitionItemActions(item: OrderItem) {
	const admin = (item.availableActions ?? []).filter((a) => a.actor === SampleActionActor.Admin);

	return {
		buttons: admin.filter((a) => a.displayType === ActionDisplayType.Button),
		notices: admin.filter((a) => a.displayType === ActionDisplayType.InlineWidget),
	};
}

/* `itemOwnerActor` lived here, and so did the rule it needed: read the live
 * sample round's actions, then the item's, skipping "Cancel item" because it is
 * never blocked. Three bugs came out of scoring action lists that way.
 *
 * `OrderItem.waitingOn` answers it now — the actor of the item's current
 * lifecycle step, resolved server-side, null when nothing is pending. See
 * `itemOwner` in OrderFlowSection. */
